import {AgEltBoxSelection, IEltBoxSelection} from "/@back@/edit/wed/features/boxSel.js";
import {EWedletEditMode, EWedletEditModeLabel, IWedlet, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IJmlNode, IJmlSubSet, JML} from "/@lib@/commons/xml/jml.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {AgEltBoxInputAnnotable, removeAnnots} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {AccelKeyMgr, Action, IAction} from "/@lib@/commons/actions.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {BASIS, MsgLabel} from "/@back@/commons/basis.js";
import {OUiCritInit, UiCritBool} from "/@back@/wsp/widgets/search/uiCrit.js";
import {XA} from "/@lib@/commons/xml/xAddr.js";
import {OSearchItemsInit, SearchItems} from "/@back@/wsp/dialogs/searchItems.js";
import {ISearchCrit, ISearchPersist, ISearchStore} from "/@lib@/wsp/search.js";
import {BoxWedlet} from "/@back@/edit/wed/wedlets/box/box.js";
import {IWspUiEnv} from "/@lib@/wsp/wsp.js";

/**
 * box-request pour l'affichage d'une requête, et l'accès aux fonctions d'édition.
 * Ex :
 * 	<box-request criterionsLists?="wsp.crit.items" fillSearchWhereWrappersLists?=""/>
 * 	<box-request criterionsLists?="wsp.crit.items" fillSearchWhereWrappersLists?=""
 * 								checkXpath?="exp[@type='xxxx']" checkMsg?="Il manque l'expression..."
 * 	/>
 */
class BoxRequest extends HTMLElement implements IEltBoxSelection, ISearchStore {

	wedlet: IWedlet;

	readonly: boolean;

	requestValue: Element;

	criterionsLists: string;

	fillSearchWhereWrappersLists: string;

	checkXpath: XPathExpression;

	checkMsg: string;

	checkResult: boolean;

	barActionsElt: BarActions<any>;

	msgLabelElt: MsgLabel;

	abstractElt: HTMLDivElement;

	configWedletElt(tpl: Element, wedlet: IWedlet & BoxWedlet) {
		this.wedlet = wedlet;
		this.readonly = !WEDLET.isWritableWedlet(wedlet);
		this.criterionsLists = BASIS.extractAttr(tpl, "criterionsLists");
		this.fillSearchWhereWrappersLists = BASIS.extractAttr(tpl, "fillSearchWhereWrappersLists");
		if (tpl.hasAttribute("checkXpath")) {
			this.checkXpath = new XPathEvaluator().createExpression(BASIS.extractAttr(tpl, "checkXpath"), this);
			this.checkMsg = BASIS.extractAttr(tpl, "checkMsg");
		}
		this.addEventListener("dblclick", this.doEdit.bind(this));
		const wedMgr = wedlet.wedMgr;
		const reg = wedMgr.reg;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		WEDLET.installSkins(tpl, sr, wedlet, this.localName);

		this.barActionsElt = sr.appendChild(<BarActions î={{
			reg,
			actionContext: this,
			actions: reg.getList<IAction<BoxRequest>>("wed:boxRequest:actions"),
			uiContext: 'dialog',
			disableFullOverlay: true
		} as OBarActionsInit<BoxRequest>}/>) as BarActions<any>;

		this.msgLabelElt = sr.appendChild(<MsgLabel class="headband"/>) as MsgLabel;
		this.abstractElt = sr.appendChild(<div id="abstract"/>) as HTMLDivElement;

		wedlet._bind = function (this: BoxWedlet, node: IJmlNode, children: IJmlSubSet) {
			this.refreshEditMode();
			this.element.refreshBindValue(node, children);
			//kill construction des binds fils.
		};
		wedlet.onChildNodesInserted = wedlet.deleteChildNodes = wedlet.updateInDescendants = () => {
			if (!this._redrawPlanned) {
				this._redrawPlanned = true;
				wedMgr.doAfterBatch(this._redraw || (this._redraw = this.redraw.bind(this)));
			}
		}
	}

	setNewFrag(elt: Element) {
		if (!elt) return this.eraseFrag();
		const wedlet = this.wedlet;
		if (!WEDLET.isWritableWedlet(wedlet)) return;

		const jml = JML.newJml();
		let vNode = elt.firstChild;
		while (vNode) {
			JML.domNode2jml(vNode, jml, null);
			vNode = vNode.nextSibling;
		}
		//console.log("setNewFrag::::", JML.jmlToDom(jml));
		const batch = wedlet.wedMgr.docHolder.newBatch();
		if (wedlet.isVirtual()) {
			WEDLET.insertDatasFromDisplay(wedlet, batch, jml);
		} else {
			//batch.setText(wedlet.wedAnchor, "");
			batch.spliceSequence(XA.append(wedlet.wedAnchor, 0), this.requestValue.children.length, jml);
		}
		batch.doBatch();
	}

	eraseFrag() {
		const wedlet = this.wedlet;
		if (wedlet.isVirtual() || this.readonly) return;
		if (!this.isEmpty())
			wedlet.wedMgr.docHolder.newBatch().deleteSequence(XA.append(wedlet.wedAnchor, 0), this.requestValue.children.length).doBatch();
	}

	setEditMode(mode: EWedletEditMode) {
		this.readonly = mode !== EWedletEditMode.write;
		DOM.setAttr(this, "edit-mode", EWedletEditModeLabel[mode]);
	}

	refreshBindValue(val: IJmlNode, children?: IJmlSubSet) {
		this.wedlet.wedMgr.doAfterBatch(() => {
			//Toute réinterrogation du document DOIT se faire après le traitement complet du batch pour que le doc et les wedlets soient en phase.
			this.requestValue = XA.findDomLast(this.wedlet.wedAnchor, this.wedlet.wedMgr.docHolder.getDocument()) as Element;
			this.redraw();
		})
	}

	protected _redraw: () => void;
	protected _redrawPlanned = false;

	redraw() {
		this._redrawPlanned = false;
		this.abstractElt.textContent = null;
		this.msgLabelElt.setStandardMsg(null);
		this.checkResult = undefined;
		// Contrôle
		if (this.checkXpath && !this.isEmpty()) {
			const result = this.checkXpath.evaluate(this.requestValue, XPathResult.BOOLEAN_TYPE);
			if (!result.booleanValue) {
				this.msgLabelElt.setCustomMsg(this.checkMsg || "Requête non valide", "warning");
				this.checkResult = false;
			} else
				this.checkResult = true;
		}
		// Maj de la requête
		const wedMgr = this.wedlet.wedMgr;
		if (this.checkResult !== false) {
			if (this.wedlet.isVirtual()) removeAnnots(this);
			if (this.isEmpty()) {
				this.msgLabelElt.setCustomMsg("Requête non définie", "info");
			} else {
				this.msgLabelElt.setStandardMsg(null);
				if (this.abstractElt) {
					const context: OUiCritInit = {
						reg: wedMgr.reg,
						criterions: this.criterionsLists ? wedMgr.reg.mergeListsAsMap(...this.criterionsLists.split(' ')) : wedMgr.reg.getListAsMap("wsp.crit.items"),
					};
					const desc = UiCritBool.AREA_AND_ROOT.buildAbstract(this.requestValue, context);
					if (desc) this.abstractElt.appendChild(desc);
				}
			}
		}
		this.barActionsElt.refreshContent();
	}

	isEmpty(): boolean {
		return !this.requestValue || this.requestValue.childNodes.length == 0;
	}

	async persistSearch(search: ISearchPersist): Promise<void> {
		//console.log("persistSearch:::::", search);
		this.setNewFrag(search.whereCrit instanceof Element ? search.whereCrit : search.whereCrit(this.wedlet.wedMgr));
	}

	async doEdit() {
		const wedMgr = this.wedlet.wedMgr;
		const reg = wedMgr.reg;
		const {SearchItems} = await import("/@back@/wsp/dialogs/searchItems.js");
		const init: OSearchItemsInit = {
			reg: reg,
			showMainView: true,
			srcGrid: {
				actions: reg.mergeLists("actions:wsp:shortDesc", "actions:searchItem:shortDesc:item"),
				accelKeyMgr: new AccelKeyMgr().initFromMapActions(reg.mergeListsAsMap("accelkeys:wsp:shortDesc", "accelkeys:searchItem:shortDesc:item"))
			},
			readonly: this.readonly || this.checkResult === false,
			criterions: this.criterionsLists ? wedMgr.reg.mergeListsAsMap(...this.criterionsLists.split(' ')) : wedMgr.reg.getListAsMap("wsp.crit.items"),
			searchStore: this,
			canEditTitle: false,
			initSearchRequest: {} as ISearchPersist,
		};
		if (!this.isEmpty()) (init.initSearchRequest as ISearchPersist).whereCrit = this.requestValue;
		if (this.fillSearchWhereWrappersLists) {
			const whereHandlers: Dict<(where: ISearchCrit[]) => Promise<void>> = wedMgr.reg.mergeListsAsMap(...this.fillSearchWhereWrappersLists.split(' ')) as Dict<(where: ISearchCrit[]) => Promise<void>>;
			init.searchWhereWrapper = async function (where: ISearchCrit[], reg: IReg<IWspUiEnv>) {
				for (const key in whereHandlers) {
					const handler = whereHandlers[key];
					await handler.call(this, where, wedMgr.reg);
				}
			};
		}

		const ct = new SearchItems().initialize(init);
		POPUP.showDialog(ct, this, {
			titleBar: {barLabel: {label: init.readonly ? 'Consultation d\'une requête' : 'Édition d\'une requête'}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			initWidth: "90vw",
			initHeight: "90vh"
		});
	}
}

AgEltBoxSelection(AgEltBoxInputAnnotable(BoxRequest), {selMode: "box"});

REG.reg.registerSkin('box-request', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: min-content;
		min-width: 5em;
		flex-direction: column;
		border: 1px solid var(--border-color);
		margin: 0;
	}

	:host(:focus) {
		outline: var(--edit-input-focus);
	}

	c-bar-actions {
		border-bottom: 1px solid var(--border-color);
	}

	#abstract {
		text-align: center;
	}
`);

window.customElements.define("box-request", BoxRequest);

class BoxRequestEdit extends Action<BoxRequest> {

	constructor() {
		super('fragEdit');
		this._description = "Éditer cette requête";
	}

	getLabel(ctx: BoxRequest): string {
		return (ctx.readonly || ctx.checkResult === false) ? "Visualiser" : "Éditer";
	}

	async execute(ctx: BoxRequest, ev?: Event) {
		await ctx.doEdit();
	}
}

class BoxRequestErase extends Action<BoxRequest> {
	constructor() {
		super('fragErase');
		this._label = "Effacer";
		this._description = "Effacer le fragment HTML";
	}

	isEnabled(ctx: BoxRequest): boolean {
		if (ctx.readonly) return false;
		return super.isEnabled(ctx);
	}

	execute(ctx: BoxRequest, ev?: Event) {
		ctx.eraseFrag();
	}
}

function addAction(action: Action<BoxRequest>) {
	REG.reg.addToList("wed:boxRequest:actions", action.getId(), 1, action);
}

addAction(new BoxRequestEdit());
addAction(new BoxRequestErase());